import { useEffect, useRef, useState } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { PrimaryButton, QuickSelectInfoButton } from "@components/v2";
import { useQuickFilters } from "@features/quick-filters";

import { QuickFilterSelector } from "../../quick-filter-selector";
import { useSalesV4Context } from "../hooks";
import { AdvancedCustomizationModal } from "../modals/advanced-customization-modal";
import { minDateForTimeGranularity } from "../utils";

import { DateOptionsPicker } from "./date-options-picker";
import { FilterTooltip } from "./filter-tooltip";

interface Props {
  showProfitabilityGaugeInfo: boolean;
  setShowProfitabilityGaugeInfo: (show: boolean) => void;
}

export const PageFilters = ({
  showProfitabilityGaugeInfo,
  setShowProfitabilityGaugeInfo,
}: Props) => {
  const { user } = useUser();
  const { quickFilters } = useQuickFilters();
  const {
    dateRange,
    compareDateRange,
    timeGranularity,
    selectedQuickFilter,
    setDateRange,
    setCompareDateRange,
    setTimeGranularity,
    applyQuickFilter,
  } = useSalesV4Context();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: "-150px 0px 0px 0px" },
    );
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const { minDate, minCompareDate } =
    minDateForTimeGranularity(timeGranularity);

  return (
    <div
      ref={headerRef}
      className={`flex px-6 items-center py-4.5 gap-2 sticky top-0 z-50 bg-v2-glass-inverse backdrop-blur-sm transition delay-150 duration-300 ${
        isSticky ? "drop-shadow-[0_0px_10px_rgba(0,0,0,0.1)]" : ""
      }`}
    >
      <DateOptionsPicker
        size="md"
        dateRange={dateRange}
        compareDateRange={compareDateRange}
        timeGranularity={timeGranularity}
        timezone={user.timezone}
        minDate={minDate}
        minCompareDate={minCompareDate}
        onChangeDateRange={(val) => setDateRange(val)}
        onChangeCompareDateRange={(val) => setCompareDateRange(val)}
        onChangeTimeGranularity={(val) => setTimeGranularity(val)}
      />
      <QuickFilterSelector
        options={quickFilters}
        value={selectedQuickFilter}
        onChange={applyQuickFilter}
        tooltipComponent={(filter) => <FilterTooltip filter={filter} />}
      />
      <PrimaryButton
        className="ml-2 text-nowrap text-sm"
        onClick={() => setIsModalOpen(true)}
      >
        New View
      </PrimaryButton>
      <AdvancedCustomizationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <QuickSelectInfoButton
        selected={showProfitabilityGaugeInfo}
        className={clsx(
          "w-9 h-9 text-xs text-v2-action hover:text-v2-action-hover",
        )}
        onClick={() => {
          setShowProfitabilityGaugeInfo(!showProfitabilityGaugeInfo);
        }}
      >
        <i className="text-sm fa-regular fa-gauge"></i>
      </QuickSelectInfoButton>
    </div>
  );
};
